<template>
  <div class="sales-analysis">
    <!-- Header Section -->
    <div class="bg-dark m-b-30">
      <div class="container">
        <div class="row p-b-30 p-t-30">
          <div class="col-md-12 text-center">
            <h2 class="text-white">
              <i class="mdi mdi-chart-areaspline"></i> Sales Analysis
            </h2>
            <p class="text-white opacity-75">Detailed sales data analysis and reporting</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Advanced Filters Section -->
    <div class="container">
      <el-card class="m-b-20">
        <div slot="header" class="card-header">
          <h4><i class="mdi mdi-filter-variant"></i> Advanced Filters</h4>
          <el-button type="text" @click="resetFilters">
            <i class="el-icon-refresh"></i> Reset
          </el-button>
        </div>

        <el-form :inline="true" size="small" label-width="100px">
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Date Range">
                <el-date-picker
                  v-model="filters.dateRange"
                  type="daterange"
                  range-separator="to"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                  format="MMM dd, yyyy"
                  value-format="yyyy-MM-dd"
                  @change="handleDateChange"
                />
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Employee">
                <el-select
                  v-model="filters.user_id"
                  multiple
                  filterable
                  placeholder="All Employees"
                  @change="loadSalesData"
                >
                  <el-option
                    v-for="user in users"
                    :key="user.id"
                    :label="user.name"
                    :value="user.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Customer">
                <el-select
                  v-model="filters.customer_id"
                  multiple
                  filterable
                  placeholder="All Customers"
                  @change="loadSalesData"
                >
                  <el-option
                    v-for="customer in customers"
                    :key="customer.id"
                    :label="customer.name"
                    :value="customer.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Payment Method">
                <el-select
                  v-model="filters.payment_method_id"
                  multiple
                  filterable
                  placeholder="All Payment Methods"
                  @change="loadSalesData"
                >
                  <el-option
                    v-for="method in paymentMethods"
                    :key="method.id"
                    :label="method.name"
                    :value="method.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Status">
                <el-select
                  v-model="filters.status"
                  placeholder="All Status"
                  @change="loadSalesData"
                >
                  <el-option label="All Status" value=""></el-option>
                  <el-option label="Paid" value="paid"></el-option>
                  <el-option label="Partial" value="partial"></el-option>
                  <el-option label="Pending" value="pending"></el-option>
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Amount Range">
                <el-input-number
                  v-model="filters.min_amount"
                  placeholder="Min"
                  :min="0"
                  style="width: 45%"
                  @change="loadSalesData"
                />
                <span style="margin: 0 5px;">-</span>
                <el-input-number
                  v-model="filters.max_amount"
                  placeholder="Max"
                  :min="0"
                  style="width: 45%"
                  @change="loadSalesData"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Sort By">
                <el-select
                  v-model="filters.sort_by"
                  placeholder="Sort by"
                  @change="loadSalesData"
                >
                  <el-option label="Date (Newest)" value="created_at_desc"></el-option>
                  <el-option label="Date (Oldest)" value="created_at_asc"></el-option>
                  <el-option label="Amount (High to Low)" value="total_price_desc"></el-option>
                  <el-option label="Amount (Low to High)" value="total_price_asc"></el-option>
                  <el-option label="Customer Name" value="customer_name"></el-option>
                  <el-option label="Employee Name" value="user_name"></el-option>
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Items Per Page">
                <el-select
                  v-model="filters.per_page"
                  placeholder="Items per page"
                  @change="loadSalesData"
                >
                  <el-option label="10" :value="10"></el-option>
                  <el-option label="25" :value="25"></el-option>
                  <el-option label="50" :value="50"></el-option>
                  <el-option label="100" :value="100"></el-option>
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item>
                <el-button type="primary" icon="el-icon-search" @click="loadSalesData" :loading="loading">
                  Search
                </el-button>
                <el-button type="success" icon="el-icon-download" @click="exportData" :loading="exporting">
                  Export
                </el-button>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>

      <!-- Summary Statistics -->
      <el-row :gutter="20" class="m-b-20">
        <el-col :xs="24" :sm="12" :md="6" v-for="stat in summaryStats" :key="stat.title" class="m-b-20">
          <el-card :body-style="{ padding: '20px' }" class="stat-card">
            <div class="stat-content">
              <div class="stat-info">
                <h4 class="stat-title">{{ stat.title }}</h4>
                <h2 class="stat-value">{{ stat.value }}</h2>
                <div class="stat-comparison">
                  <span :class="['trend', stat.trend > 0 ? 'positive' : 'negative']">
                    <i :class="stat.trend > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                    {{ Math.abs(stat.trend) }}%
                  </span>
                  <span class="comparison-text">vs filtered results</span>
                </div>
              </div>
              <div class="stat-icon">
                <i :class="stat.icon"></i>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- Loading State -->
      <div v-if="loading" class="text-center p-40">
        <i class="el-icon-loading" style="font-size: 24px;"></i>
        <p>Loading sales data...</p>
      </div>

      <!-- Sales Data Table -->
      <el-card v-else>
        <div slot="header" class="card-header">
          <h4><i class="mdi mdi-receipt"></i> Sales Data</h4>
          <div class="header-actions">
            <el-tag type="info">{{ pagination.total }} records found</el-tag>
            <el-button-group>
              <el-button size="mini" @click="toggleViewMode">
                <i :class="viewMode === 'table' ? 'el-icon-menu' : 'el-icon-s-grid'"></i>
                {{ viewMode === 'table' ? 'Grid' : 'Table' }}
              </el-button>
            </el-button-group>
          </div>
        </div>

        <!-- Table View -->
        <div v-if="viewMode === 'table'">
          <el-table
            :data="salesData"
            style="width: 100%"
            size="small"
            @sort-change="handleSortChange"
            @selection-change="handleSelectionChange"
          >
            <el-table-column type="selection" width="55" />
            <el-table-column prop="code" label="Order ID" width="120" sortable="custom" />
            <el-table-column label="Customer" width="200" sortable="custom">
              <template slot-scope="scope">
                <div class="customer-info">
                  <span class="customer-name">{{ scope.row.customer?.name || 'Walk-in Customer' }}</span>
                  <span v-if="scope.row.customer?.phone" class="customer-phone">
                    {{ scope.row.customer.phone }}
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Employee" width="150" sortable="custom">
              <template slot-scope="scope">
                <div class="employee-info">
                  <span class="employee-name">{{ scope.row.user?.name || 'Unknown' }}</span>
                  <span class="employee-role">{{ scope.row.user?.role || 'Cashier' }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Items" width="100">
              <template slot-scope="scope">
                <el-tooltip :content="getItemsTooltip(scope.row)" placement="top">
                  <span>{{ scope.row.invoices?.length || 0 }} items</span>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="Amount" width="120" sortable="custom">
              <template slot-scope="scope">
                <div class="amount-info">
                  <span class="total-amount">{{ formatCurrency(scope.row.total_price) }}</span>
                  <span v-if="scope.row.discount > 0" class="discount-amount">
                    -{{ formatCurrency(scope.row.discount) }}
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Payment" width="150">
              <template slot-scope="scope">
                <div class="payment-info">
                  <span class="paid-amount">{{ formatCurrency(scope.row.paid) }}</span>
                  <span v-if="scope.row.balance > 0" class="balance-amount">
                    Balance: {{ formatCurrency(scope.row.balance) }}
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Status" width="100">
              <template slot-scope="scope">
                <el-tag
                  :type="getStatusType(scope.row)"
                  size="mini"
                >
                  {{ getStatusText(scope.row) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Payment Methods" width="150">
              <template slot-scope="scope">
                <div class="payment-methods">
                  <el-tag
                    v-for="payment in scope.row.payments"
                    :key="payment.id"
                    size="mini"
                    type="info"
                    style="margin: 2px"
                  >
                    {{ payment.name }}: {{ formatCurrency(payment.pivot.amount) }}
                  </el-tag>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Date" width="150" sortable="custom">
              <template slot-scope="scope">
                <div class="date-info">
                  <span class="date">{{ formatDate(scope.row.created_at) }}</span>
                  <span class="time">{{ formatTime(scope.row.created_at) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Actions" width="120" fixed="right">
              <template slot-scope="scope">
                <el-button-group>
                  <el-button
                    size="mini"
                    type="primary"
                    icon="el-icon-view"
                    @click="viewSaleDetails(scope.row)"
                  />
                  <el-button
                    size="mini"
                    type="success"
                    icon="el-icon-printer"
                    @click="printReceipt(scope.row)"
                  />
                </el-button-group>
              </template>
            </el-table-column>
          </el-table>

          <!-- Pagination -->
          <div class="pagination-container">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="pagination.current_page"
              :page-sizes="[10, 25, 50, 100]"
              :page-size="+pagination.per_page"
              layout="total, sizes, prev, pager, next, jumper"
              :total="pagination.total"
            />
          </div>
        </div>

        <!-- Grid View -->
        <div v-else class="grid-view">
          <el-row :gutter="20">
            <el-col
              v-for="sale in salesData"
              :key="sale.id"
              :xs="24"
              :sm="12"
              :md="8"
              :lg="6"
              class="m-b-20"
            >
              <el-card :body-style="{ padding: '15px' }" class="sale-card">
                <div class="sale-header">
                  <h4 class="sale-code">{{ sale.code }}</h4>
                  <el-tag :type="getStatusType(sale)" size="mini">
                    {{ getStatusText(sale) }}
                  </el-tag>
                </div>

                <div class="sale-info">
                  <div class="info-row">
                    <span class="label">Customer:</span>
                    <span class="value">{{ sale.customer?.name || 'Walk-in Customer' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">Employee:</span>
                    <span class="value">{{ sale.user?.name || 'Unknown' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">Items:</span>
                    <span class="value">{{ sale.invoices?.length || 0 }} items</span>
                  </div>
                  <div class="info-row">
                    <span class="label">Amount:</span>
                    <span class="value amount">{{ formatCurrency(sale.total_price) }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">Paid:</span>
                    <span class="value">{{ formatCurrency(sale.paid) }}</span>
                  </div>
                  <div v-if="sale.balance > 0" class="info-row">
                    <span class="label">Balance:</span>
                    <span class="value balance">{{ formatCurrency(sale.balance) }}</span>
                  </div>
                </div>

                <div class="sale-footer">
                  <span class="date">{{ formatDate(sale.created_at) }}</span>
                  <div class="actions">
                    <el-button size="mini" type="primary" icon="el-icon-view" @click="viewSaleDetails(sale)" />
                    <el-button size="mini" type="success" icon="el-icon-printer" @click="printReceipt(sale)" />
                  </div>
                </div>
              </el-card>
            </el-col>
          </el-row>
        </div>
      </el-card>
    </div>

    <!-- Sale Details Dialog -->
    <el-dialog
      title="Sale Details"
      :visible.sync="saleDetailsVisible"
      width="70%"
      :before-close="handleCloseDialog"
    >
      <div v-if="selectedSale" class="sale-details">
        <el-row :gutter="20">
          <el-col :span="12">
            <h4>Sale Information</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Order ID">{{ selectedSale.code }}</el-descriptions-item>
              <el-descriptions-item label="Customer">{{ selectedSale.customer?.name || 'Walk-in Customer' }}</el-descriptions-item>
              <el-descriptions-item label="Employee">{{ selectedSale.user?.name || 'Unknown' }}</el-descriptions-item>
              <el-descriptions-item label="Date">{{ formatDateTime(selectedSale.created_at) }}</el-descriptions-item>
              <el-descriptions-item label="Status">
                <el-tag :type="getStatusType(selectedSale)">{{ getStatusText(selectedSale) }}</el-tag>
              </el-descriptions-item>
            </el-descriptions>
          </el-col>

          <el-col :span="12">
            <h4>Financial Summary</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Total Amount">{{ formatCurrency(selectedSale.total_price) }}</el-descriptions-item>
              <el-descriptions-item label="Discount">{{ formatCurrency(selectedSale.discount) }}</el-descriptions-item>
              <el-descriptions-item label="Paid Amount">{{ formatCurrency(selectedSale.paid) }}</el-descriptions-item>
              <el-descriptions-item label="Balance">{{ formatCurrency(selectedSale.balance) }}</el-descriptions-item>
            </el-descriptions>
          </el-col>
        </el-row>

        <el-divider content-position="left">Items</el-divider>

        <el-table :data="selectedSale.invoices || []" size="small">
          <el-table-column prop="item.name" label="Item" />
          <el-table-column prop="quantity" label="Qty" width="80" />
          <el-table-column prop="unit_price" label="Unit Price" width="120">
            <template slot-scope="scope">
              {{ formatCurrency(scope.row.unit_price) }}
            </template>
          </el-table-column>
          <el-table-column label="Total" width="120">
            <template slot-scope="scope">
              {{ formatCurrency(scope.row.quantity * scope.row.unit_price) }}
            </template>
          </el-table-column>
        </el-table>

        <el-divider content-position="left">Payment Methods</el-divider>

        <div class="payment-methods-detail">
          <el-tag
            v-for="payment in selectedSale.payments"
            :key="payment.id"
            type="info"
            style="margin: 5px"
          >
            {{ payment.name }}: {{ formatCurrency(payment.pivot.amount) }}
          </el-tag>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="saleDetailsVisible = false">Close</el-button>
        <el-button type="primary" @click="printReceipt(selectedSale)">Print Receipt</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'Sales',
  mixins: [ErrorHandlingMixin],
  data () {
    return {
      loading: false,
      exporting: false,
      viewMode: 'table',
      saleDetailsVisible: false,
      selectedSale: null,
      selectedSales: [],
      salesData: [],
      pagination: {
        current_page: 1,
        per_page: 25,
        total: 0,
        last_page: 1
      },
      filters: {
        dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
        user_id: [],
        customer_id: [],
        payment_method_id: [],
        status: '',
        min_amount: null,
        max_amount: null,
        sort_by: 'created_at_desc',
        per_page: 25
      },
      users: [],
      customers: [],
      paymentMethods: []
    }
  },
  computed: {
    summaryStats () {
      const totalSales = this.salesData.reduce((sum, sale) => sum + sale.total_price, 0)
      const totalPaid = this.salesData.reduce((sum, sale) => sum + sale.paid, 0)
      const totalBalance = this.salesData.reduce((sum, sale) => sum + sale.balance, 0)
      const totalDiscount = this.salesData.reduce((sum, sale) => sum + sale.discount, 0)

      return [
        {
          title: 'Total Sales',
          value: this.formatCurrency(totalSales),
          trend: 12.5,
          icon: 'mdi mdi-currency-fra'
        },
        {
          title: 'Total Paid',
          value: this.formatCurrency(totalPaid),
          trend: 8.3,
          icon: 'mdi mdi-check-circle'
        },
        {
          title: 'Outstanding',
          value: this.formatCurrency(totalBalance),
          trend: -5.2,
          icon: 'mdi mdi-clock-outline'
        },
        {
          title: 'Total Discount',
          value: this.formatCurrency(totalDiscount),
          trend: 15.7,
          icon: 'mdi mdi-tag-outline'
        }
      ]
    }
  },
  mounted () {
    this.loadInitialData()
  },
  methods: {
    getDefaultStartDate () {
      const date = new Date()
      date.setDate(date.getDate() - 30)
      return date.toISOString().split('T')[0]
    },
    getDefaultEndDate () {
      return new Date().toISOString().split('T')[0]
    },
    handleDateChange (dates) {
      if (dates && dates.length === 2) {
        this.filters.start_date = dates[0]
        this.filters.end_date = dates[1]
        this.pagination.current_page = 1
        this.loadSalesData()
      }
    },
    resetFilters () {
      this.filters = {
        dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
        user_id: [],
        customer_id: [],
        payment_method_id: [],
        status: '',
        min_amount: null,
        max_amount: null,
        sort_by: 'created_at_desc',
        per_page: 25
      }
      this.pagination.current_page = 1
      this.loadSalesData()
    },
    async loadInitialData () {
      try {
        await Promise.all([
          this.loadUsers(),
          this.loadCustomers(),
          this.loadPaymentMethods()
        ])
        await this.loadSalesData()
      } catch (error) {
        this.handleError(error, 'Failed to load initial data')
      }
    },
    async loadSalesData () {
      this.loading = true
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const params = {
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        }

        const response = await this.$reportService.getSalesDetailed(params)
        if (response.status === 'success') {
          this.salesData = response.data.data
          this.pagination = {
            current_page: response.data.current_page,
            per_page: response.data.per_page,
            total: response.data.total,
            last_page: response.data.last_page
          }
        }
      } catch (error) {
        this.handleError(error, 'Failed to load sales data')
      } finally {
        this.loading = false
      }
    },
    async loadUsers () {
      try {
        this.users = await this.$userService.getUsers()
      } catch (error) {
        console.error('Error loading users:', error)
      }
    },
    async loadCustomers () {
      try {
        this.customers = await this.$customerService.getCustomers()
      } catch (error) {
        console.error('Error loading customers:', error)
      }
    },
    async loadPaymentMethods () {
      try {
        this.paymentMethods = await this.$paymentMethodService.getMethods()
      } catch (error) {
        console.error('Error loading payment methods:', error)
      }
    },
    async exportData () {
      this.exporting = true
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const params = {
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          dl: 'pdf'
        }

        const response = await this.$reportService.getSalesDetailed(params)
        if (response.url) {
          window.open(response.url, '_blank')
        }
      } catch (error) {
        this.handleError(error, 'Failed to export data')
      } finally {
        this.exporting = false
      }
    },
    handleSortChange ({ column, prop, order }) {
      // Handle table sorting
      this.loadSalesData()
    },
    handleSelectionChange (selection) {
      this.selectedSales = selection
    },
    handleSizeChange (size) {
      this.pagination.per_page = size
      this.loadSalesData()
    },
    handleCurrentChange (page) {
      this.pagination.current_page = page
      this.loadSalesData()
    },
    toggleViewMode () {
      this.viewMode = this.viewMode === 'table' ? 'grid' : 'table'
    },
    viewSaleDetails (sale) {
      this.selectedSale = sale
      this.saleDetailsVisible = true
    },
    printReceipt (sale) {
      // Implement receipt printing
      console.log('Printing receipt for sale:', sale.code)
    },
    handleCloseDialog () {
      this.saleDetailsVisible = false
      this.selectedSale = null
    },
    getStatusType (sale) {
      if (sale.balance === 0) return 'success'
      if (sale.balance > 0 && sale.paid > 0) return 'warning'
      return 'danger'
    },
    getStatusText (sale) {
      if (sale.balance === 0) return 'Paid'
      if (sale.balance > 0 && sale.paid > 0) return 'Partial'
      return 'Pending'
    },
    getItemsTooltip (sale) {
      if (!sale.invoices || sale.invoices.length === 0) return 'No items'

      return sale.invoices.map(invoice =>
        `${invoice.item?.name || 'Unknown Item'} (${invoice.quantity})`
      ).join('\n')
    },
    formatCurrency (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'XAF'
      }).format(value)
    },
    formatDate (dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    formatTime (dateString) {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatDateTime (dateString) {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.sales-analysis {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.stat-card {
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.stat-info {
  flex: 1;
}

.stat-title {
  color: #606266;
  font-size: 14px;
  margin: 0 0 8px 0;
}

.stat-value {
  color: #303133;
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 8px 0;
}

.stat-comparison {
  display: flex;
  align-items: center;
  gap: 4px;
}

.trend {
  font-size: 12px;
  font-weight: bold;
}

.trend.positive {
  color: #67C23A;
}

.trend.negative {
  color: #F56C6C;
}

.comparison-text {
  font-size: 12px;
  color: #909399;
}

.stat-icon {
  font-size: 32px;
  color: #409EFF;
  opacity: 0.8;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.customer-info, .employee-info {
  display: flex;
  flex-direction: column;
}

.customer-name, .employee-name {
  font-weight: 500;
  color: #303133;
}

.customer-phone, .employee-role {
  font-size: 12px;
  color: #909399;
}

.amount-info {
  display: flex;
  flex-direction: column;
}

.total-amount {
  font-weight: 500;
  color: #303133;
}

.discount-amount {
  font-size: 12px;
  color: #F56C6C;
}

.payment-info {
  display: flex;
  flex-direction: column;
}

.paid-amount {
  font-weight: 500;
  color: #67C23A;
}

.balance-amount {
  font-size: 12px;
  color: #E6A23C;
}

.payment-methods {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.date-info {
  display: flex;
  flex-direction: column;
}

.date {
  font-weight: 500;
  color: #303133;
}

.time {
  font-size: 12px;
  color: #909399;
}

.pagination-container {
  margin-top: 20px;
  text-align: center;
}

.grid-view {
  margin-top: 20px;
}

.sale-card {
  transition: all 0.3s ease;
}

.sale-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.sale-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.sale-code {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.sale-info {
  margin-bottom: 15px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.label {
  font-size: 12px;
  color: #909399;
}

.value {
  font-size: 12px;
  color: #303133;
  font-weight: 500;
}

.value.amount {
  color: #409EFF;
  font-weight: bold;
}

.value.balance {
  color: #E6A23C;
  font-weight: bold;
}

.sale-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #EBEEF5;
}

.date {
  font-size: 12px;
  color: #909399;
}

.actions {
  display: flex;
  gap: 5px;
}

.payment-methods-detail {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.text-muted {
  color: #909399;
}

.p-40 {
  padding: 40px;
}

.m-b-20 {
  margin-bottom: 20px;
}

.m-b-30 {
  margin-bottom: 30px;
}

.p-b-30 {
  padding-bottom: 30px;
}

.p-t-30 {
  padding-top: 30px;
}
</style>
